import React, { useMemo, useContext, useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import CustomTable from '../../../common/Table/CustomTable/CustomTable';
import { columns, monthly1columns, monthly2columns, monthly3columns } from './helpers';
import {
  addNewTimesheet,
  changePartOfPerDiem,
  changePerDiem,
  checkUncheckAllRows,
  updateTimesheet
} from '../../../../actions/timesheet';
// eslint-disable-next-line import/no-cycle
import TimesheetTableWrapper from './TimesheetTableWrapper/TimesheetTableWrapper';
import { useWindowSize } from '../../../../utils/hooks';
import { responsiveSizeTimesheet, timesheetsStatuses } from '../../../../utils/constants';
import TimesheetsCardList from './TimesheetsCardList/TimesheetsCardList';
// eslint-disable-next-line import/no-cycle
import { EditContext } from '../Timesheet';
import NotificationModal from '../../../common/ModalWindows/NotificationModal';
import classes from './MAinTable.module.scss';
import PerDiemCreationComponent from '../../../common/PerDiemCreationComponent/PerDiemCreationComponent';
import { getDatesForWeek, getIsPerDiemByDate } from '../helpers';
import colors from '../../../../scss/colors';
import { clearMonth } from '../../../../utils/helpers';
import { submissionEnum } from '../../../../utils/enums';

const PerDiemInfoComponent = ({ perdiems, datesForWeek, timesheetType, choosedMonth, setScrollablePerDiem }) => {
  const { t } = useTranslation();
  const refPerDiem = useRef(null);

  useEffect(() => {
    setScrollablePerDiem(refPerDiem);
  }, [refPerDiem]);

  return timesheetType === submissionEnum.WEEKLY ? (
    <div className={classes.perdiemsInfoContainer}>
      <p>{t('dashboard.timesheet.perdiem')}</p>
      <div className={classes.valuesContainer}>
        {datesForWeek.map((date) => {
          const value = getIsPerDiemByDate(perdiems, date);
          return value ? (
            <CheckIcon key={date.toString()} sx={{ fontSize: '16px', color: colors.PRIMARY_BLUE }} />
          ) : (
            <div key={date.toString()} />
          );
        })}
      </div>
    </div>
  ) : (
    <div className={`${classes.perdiemsInfoContainer} ${classes.monthly}`}>
      <p>{t('dashboard.timesheet.perdiem')}</p>
      <div className={`${classes.divider} ${classes.left}`} />
      <div ref={refPerDiem} className={classes.valuesContainerMonthly}>
        {perdiems
          .filter((item) => dayjs(item.Date).month() === choosedMonth)
          .map((perDiemItem) => (
            <CheckIcon
              key={perDiemItem.Date}
              sx={{ fontSize: '16px', color: perDiemItem.IsPerDiem ? colors.PRIMARY_BLUE : colors.WHITE }}
            />
          ))}
      </div>
      <div className={`${classes.divider} ${classes.right}`} />
      <p>{perdiems.reduce((count, pd) => (pd.IsPerDiem ? count + 1 : count), 0)}</p>
    </div>
  );
};

export default function MainTable({
  data,
  perdiems,
  startDateOfPeriod,
  submissionType,
  rateType,
  saving,
  rejecting,
  save,
  submit,
  approve,
  reject,
  isRejectActive,
  deleteLine,
  timesheetInfo,
  setScrollableRef,
  setScrollablePerDiem,
  scrollByScroll,
  scroll
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const size = useWindowSize();
  const ctx = useContext(EditContext);

  const ref = useRef(null);
  const scrollByScrollRef = useRef(null);
  const scrollByScrollPerDiemRef = useRef(null);

  const [openRateError, setOpenRateError] = useState('');

  const allowedWorkTypes = useSelector((state) =>
    state.constFromApi.worktypes?.map((item) => ({
      Id: item.value,
      Name: item.label
    }))
  );

  const isAllItemsChecked = useMemo(() => data.every((item) => item.checked === true), [data]);

  const currentMonth = new Date(
    data[0].TimesheetHoursByDates.find((item) => new Date(item?.Day).getDate() === 1)?.Day
  ).getMonth();

  const datesForWeek = getDatesForWeek(startDateOfPeriod);

  const changeHandler = (type, id, newValue, additional) => {
    let timesheet = data.find((sheet) => sheet.Id === id);

    if (type === 'TimesheetHoursByDates') {
      timesheet = {
        ...timesheet,
        TimesheetHoursByDates: timesheet.TimesheetHoursByDates.some((item) => dayjs(item.Day).isSame(dayjs(additional)))
          ? timesheet.TimesheetHoursByDates.map((item) =>
              dayjs(item.Day).isSame(dayjs(additional)) ? { Day: item.Day, TotalHrs: newValue || 0 } : item
            )
          : [...timesheet.TimesheetHoursByDates, { Day: additional, TotalHrs: newValue || 0 }]
      };
    } else {
      switch (type) {
        case 'Location':
          timesheet = {
            ...timesheet,
            Location: newValue
          };
          break;
        case 'WorkType':
          timesheet = {
            ...timesheet,
            WorkType: newValue
          };
          break;
        case 'Comment':
          timesheet = {
            ...timesheet,
            Comment: newValue
          };
          break;
        case 'checked':
          timesheet = {
            ...timesheet,
            checked: newValue
          };
          break;
        default:
          break;
      }
    }

    dispatch(updateTimesheet(timesheet));
    ctx.setHasChanges(true);
  };

  const mobileChangeHandler = (newData) => {
    dispatch(updateTimesheet(newData));
    ctx.setHasChanges(true);
  };

  const mainCheckboxHandler = (value) => {
    const timesheets = data.map((item) => ({ ...item, checked: value }));

    dispatch(checkUncheckAllRows(timesheets));
  };

  const addHandler = () => {
    dispatch(addNewTimesheet());
    ctx.setHasChanges(true);
  };

  const changePerDiemHandler = (date, value) => {
    dispatch(changePerDiem({ Date: dayjs(date).format('YYYY-MM-DD'), IsPerDiem: value }));
    ctx.setHasChanges(true);
  };

  const massiveChangePerDiem = (newPart) => {
    dispatch(changePartOfPerDiem(newPart));
    ctx.setHasChanges(true);
  };

  useEffect(() => {
    setScrollableRef(ref);
  }, [ref]);

  useEffect(() => {
    scrollByScrollRef?.current?.addEventListener('wheel', scrollByScroll, { passive: false });
    return () => {
      scrollByScrollRef?.current?.removeEventListener('wheel', scrollByScroll, { passive: false });
    };
  }, [
    scrollByScrollRef,
    scrollByScrollRef?.current,
    scrollByScrollRef,
    scrollByScrollRef?.current,
    scrollByScrollPerDiemRef,
    scrollByScrollPerDiemRef?.current,
    scroll
  ]);

  return (
    <div ref={scrollByScrollRef} style={{ marginTop: '32px', width: '100%' }}>
      <TimesheetTableWrapper
        data={data}
        submissionType={submissionType}
        saving={saving}
        rejecting={rejecting}
        save={save}
        submit={submit}
        approve={approve}
        reject={reject}
        isRejectActive={isRejectActive}
        timesheetInfo={timesheetInfo}
        currentMonth={currentMonth}
      >
        {size.width > responsiveSizeTimesheet ? (
          <div className={classes.desktopTableContainer}>
            {submissionType === submissionEnum.WEEKLY ? (
              <CustomTable
                data={data}
                columns={() =>
                  columns(
                    startDateOfPeriod,
                    changeHandler,
                    t,
                    isAllItemsChecked,
                    mainCheckboxHandler,
                    addHandler,
                    allowedWorkTypes,
                    ctx.isEdit,
                    deleteLine,
                    rateType,
                    currentMonth,
                    submissionType,
                    setOpenRateError,
                    timesheetInfo.status,
                    timesheetInfo.user
                  )
                }
                withFooter
                withLines={{
                  header: false,
                  body: false,
                  footer: false
                }}
                footerMargin="0"
                bodyWithBorderRadius={false}
              />
            ) : (
              <div className={classes.auxContainer}>
                <CustomTable
                  data={data}
                  columns={() =>
                    monthly1columns(
                      startDateOfPeriod,
                      changeHandler,
                      t,
                      isAllItemsChecked,
                      mainCheckboxHandler,
                      addHandler,
                      allowedWorkTypes,
                      ctx.isEdit,
                      deleteLine,
                      rateType,
                      currentMonth,
                      submissionType,
                      setOpenRateError,
                      timesheetInfo.status,
                      timesheetInfo.user
                    )
                  }
                  withFooter
                  withLines={{
                    header: false,
                    body: false,
                    footer: false
                  }}
                  headerStyles={{
                    borderRadius: '10px 0 0 0',
                    height: '68px',
                    borderRight: `2px solid ${colors.DARKEST_NAVY_BLUE}`
                  }}
                  bodyStyles={{
                    padding: '20px 26px 0 26px',
                    backgroundColor: colors.WHITE,
                    borderRight: `2px solid ${colors.PAGE_BACKGROUND}`
                  }}
                  footerStyles={{
                    borderRight: `2px solid ${colors.PAGE_BACKGROUND}`
                  }}
                  wrapperStyles={{
                    maxWidth: 'max-content',
                    backgroundColor: colors.WHITE,
                    borderRadius: '0'
                  }}
                  footerMargin="0"
                  bodyWithBorderRadius
                />
                <div ref={ref} className={classes.scrollableContainer}>
                  <CustomTable
                    data={clearMonth(data, currentMonth)}
                    columns={() =>
                      monthly2columns(
                        startDateOfPeriod,
                        changeHandler,
                        t,
                        isAllItemsChecked,
                        mainCheckboxHandler,
                        addHandler,
                        allowedWorkTypes,
                        ctx.isEdit,
                        deleteLine,
                        rateType,
                        currentMonth,
                        submissionType,
                        setOpenRateError,
                        clearMonth(data, currentMonth)
                      )
                    }
                    withFooter
                    withLines={{
                      header: false,
                      body: false,
                      footer: false
                    }}
                    headerStyles={{
                      height: '68px',
                      borderRadius: '0',
                      padding: '20px 8px 0 8px'
                    }}
                    wrapperStyles={{
                      backgroundColor: colors.WHITE,
                      borderRadius: '0',
                      boxShadow: '2px 0px 5px 0px rgba(79, 79, 79, 0.10)'
                    }}
                    bodyStyles={{
                      padding: '20px 8px 0 8px',
                      backgroundColor: colors.WHITE
                    }}
                    footerStyles={{
                      padding: '0 8px 20px 8px',
                      backgroundColor: colors.WHITE
                    }}
                    footerMargin="0"
                    bodyWithBorderRadius={false}
                  />
                </div>
                <CustomTable
                  data={data}
                  columns={() =>
                    monthly3columns(
                      startDateOfPeriod,
                      changeHandler,
                      t,
                      isAllItemsChecked,
                      mainCheckboxHandler,
                      addHandler,
                      allowedWorkTypes,
                      ctx.isEdit,
                      deleteLine,
                      rateType,
                      currentMonth,
                      submissionType,
                      setOpenRateError,
                      timesheetInfo.status,
                      timesheetInfo.user
                    )
                  }
                  withFooter
                  withLines={{
                    header: false,
                    body: false,
                    footer: false
                  }}
                  footerMargin="0"
                  headerStyles={{
                    borderRadius: '0 10px 0 0',
                    height: '68px',
                    borderLeft: `2px solid ${colors.DARKEST_NAVY_BLUE}`
                  }}
                  bodyStyles={{
                    padding: '20px 26px 0 26px',
                    backgroundColor: colors.WHITE,
                    borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`
                  }}
                  footerStyles={{
                    padding: '0 26px 20px 26px',
                    backgroundColor: colors.WHITE,
                    borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`
                  }}
                  wrapperStyles={{
                    maxWidth: 'max-content',
                    backgroundColor: colors.WHITE,
                    borderRadius: '0 0 10px 0',
                    boxShadow: 'none'
                  }}
                  bodyWithBorderRadius
                />
              </div>
            )}
            {timesheetInfo.isApprover ||
            (!timesheetInfo.isApprover &&
              [timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
                timesheetInfo.status
              )) ? (
              <PerDiemInfoComponent
                perdiems={perdiems}
                datesForWeek={datesForWeek}
                timesheetType={submissionType}
                choosedMonth={new Date(data?.[0].TimesheetHoursByDates[6].Day).getMonth()}
                setScrollablePerDiem={(value) => {
                  setScrollablePerDiem(value);
                  scrollByScrollPerDiemRef.current = value;
                }}
              />
            ) : (
              <PerDiemCreationComponent
                perdiems={perdiems}
                startDateOfPeriod={startDateOfPeriod}
                timesheetType={submissionType}
                changePerdiem={changePerDiemHandler}
                setScrollablePerDiem={(value) => {
                  setScrollablePerDiem(value);
                  scrollByScrollPerDiemRef.current = value;
                }}
                choosedMonth={new Date(data?.[0].TimesheetHoursByDates[6].Day).getMonth()}
                fullDisabled={
                  !ctx.isEdit ||
                  [timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
                    timesheetInfo.status
                  )
                }
                extraClass={classes.extraPerDiemsContainer}
              />
            )}
          </div>
        ) : (
          <TimesheetsCardList
            data={data}
            perdiems={perdiems}
            massiveChangePerDiem={massiveChangePerDiem}
            allowedWorkTypes={allowedWorkTypes}
            startDateOfPeriod={startDateOfPeriod}
            addHandler={addHandler}
            deleteLineHandler={deleteLine}
            mobileChangeHandler={mobileChangeHandler}
            rateType={rateType}
            submissionType={submissionType}
            currentMonth={currentMonth}
            editable={
              ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
                timesheetInfo.status
              ) && !timesheetInfo.isApprover
            }
            status={timesheetInfo.status}
            author={timesheetInfo.user}
          />
        )}
      </TimesheetTableWrapper>
      {openRateError && (
        <NotificationModal open={openRateError} onClose={() => setOpenRateError('')}>
          <p className={classes.errorMessage}>{openRateError}</p>
        </NotificationModal>
      )}
    </div>
  );
}
